import type { Temporal } from '@js-temporal/polyfill'
import { useEventListener } from '@vueuse/core'
import type { ComputedRef, Ref } from 'vue'
import { computed, ref, shallowRef } from 'vue'
import { useBoolean } from '@/src/hooks/useBoolean'
import { MAX_WORK_TO_MINUTES, TIME_LINE_INTERVAL_MINUTES } from '@/src/lib/constants'
import { minutesToPlainTime, plainTimeToMinutes } from '@/src/lib/dateTimeUtils'
import { pxToIntervalMinutes } from '@/src/lib/timelineUtils'

type ResizeEdge = 'start' | 'end'

type OnEndArgs = {
  from: Temporal.PlainTime
  to: Temporal.PlainTime
}

type UseResizingScheduleBarOptions = {
  from: Ref<Temporal.PlainTime>
  to: Ref<Temporal.PlainTime>
  enabled: Ref<boolean>
  onEnd: (args: OnEndArgs) => Promise<void>
}

type UseResizingScheduleBarReturn = {
  resizing: ComputedRef<boolean>
  // NOTE: リサイズ中およびサーバー反映待ちの間は変更後の時刻、それ以外は元の時刻を返す
  previewFrom: ComputedRef<Temporal.PlainTime>
  previewTo: ComputedRef<Temporal.PlainTime>
  startResize: (edge: ResizeEdge, event: MouseEvent) => void
  // NOTE: 直前の操作がリサイズだったか。リサイズ由来のclickをバー本体で無視するために使う
  justResized: Readonly<Ref<boolean>>
}

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max)

const isSameTimeRange = (left: OnEndArgs, right: OnEndArgs): boolean =>
  left.from.equals(right.from) && left.to.equals(right.to)

export const useResizingScheduleBar = (options: UseResizingScheduleBarOptions): UseResizingScheduleBarReturn => {
  const edge = ref<ResizeEdge>()
  const originX = ref(0)
  const currentX = ref(0)
  // NOTE: マウスを離してから更新が反映されるまで元の幅に戻ってしまわないように保持する
  // 反映待ちを識別に使うためshallowRefで生の値のまま扱う
  const pending = shallowRef<OnEndArgs>()
  // NOTE: mouseupの直後に発火するclickはmousedownとmouseupの共通祖先(=バー本体)が対象になるため、
  // ハンドル側でstopPropagationしても編集モーダルが開いてしまう。バー本体のclickをこのフラグで無視させる
  const [justResized, setJustResized] = useBoolean(false)
  const resizing = computed<boolean>(() => edge.value !== undefined)
  // NOTE: リサイズできないバーではリスナーを張らない(タイムラインにはバーが人数分並ぶため)
  const listenerTarget = computed(() => (options.enabled.value ? globalThis : undefined))

  const deltaMinutes = computed<number>(() => pxToIntervalMinutes(currentX.value - originX.value))

  // NOTE: 反映待ちの間はpropsが更新前のままなので、続けて掴み直したときは表示中の時刻を起点にする
  const baseFrom = computed<Temporal.PlainTime>(() => pending.value?.from ?? options.from.value)
  const baseTo = computed<Temporal.PlainTime>(() => pending.value?.to ?? options.to.value)

  const previewFrom = computed<Temporal.PlainTime>(() => {
    if (edge.value !== 'start') return baseFrom.value

    // NOTE: 終了時刻を追い越さないよう最小幅(15分)を残す
    const max = plainTimeToMinutes(baseTo.value) - TIME_LINE_INTERVAL_MINUTES
    return minutesToPlainTime(clamp(plainTimeToMinutes(baseFrom.value) + deltaMinutes.value, 0, max))
  })

  const previewTo = computed<Temporal.PlainTime>(() => {
    if (edge.value !== 'end') return baseTo.value

    const min = plainTimeToMinutes(baseFrom.value) + TIME_LINE_INTERVAL_MINUTES
    return minutesToPlainTime(clamp(plainTimeToMinutes(baseTo.value) + deltaMinutes.value, min, MAX_WORK_TO_MINUTES))
  })

  const startResize = (target: ResizeEdge, event: MouseEvent): void => {
    if (!options.enabled.value) return

    edge.value = target
    originX.value = event.clientX
    currentX.value = event.clientX
  }

  // NOTE: リサイズ由来のclickはmouseupの直後に発火するので、次のmousedownまで残っていればもう用済み。
  // バー本体は@mousedown.stopしているためcaptureで受ける
  useEventListener<MouseEvent>(listenerTarget, 'mousedown', setJustResized.off, { capture: true })

  // NOTE: リサイズ中はカーソルがバーの外に出るのでwindowで追従する
  useEventListener<MouseEvent>(listenerTarget, 'mousemove', (event) => {
    if (!resizing.value) return

    currentX.value = event.clientX
  })

  useEventListener<MouseEvent>(listenerTarget, 'mouseup', async () => {
    if (!resizing.value) return

    const resized = { from: previewFrom.value, to: previewTo.value }
    edge.value = undefined
    setJustResized.on()

    // NOTE: 掴んだだけで動かさなかった場合は更新しない
    if (isSameTimeRange(resized, { from: baseFrom.value, to: baseTo.value })) return

    pending.value = resized
    try {
      await options.onEnd(resized)
    } finally {
      // NOTE: 更新に失敗した場合はpropsが変わらないため元の幅に戻る
      // 後続のリサイズが反映待ちの場合はそちらのプレビューを消さない
      if (pending.value === resized) pending.value = undefined
    }
  })

  return { resizing, previewFrom, previewTo, startResize, justResized }
}
